import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Loader2, User, Building2, ChevronRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { GoldButton, Logo, inputClass, labelClass } from "../components/shared/UIComponents";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];
const PROPERTY_TYPES = ["Single Family", "Multi-Family", "Commercial", "Mixed Use", "Land", "Condo/Townhome"];
const STRATEGIES = ["Fix & Flip", "Buy & Hold", "BRRRR", "Wholesale", "New Construction", "Value-Add", "Cash Flow"];
const ENTITY_TYPES = ["LLC", "S-Corp", "C-Corp", "Partnership", "Sole Proprietorship", "Trust"];

export default function OnboardingPage({ onComplete }) {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    full_name: user?.full_name || "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    experience: "",
    property_types: [],
    investment_strategy: "",
    company_name: "",
    entity_type: "",
    ein: "",
    company_address: "",
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const togglePropType = (val) => {
    set("property_types", form.property_types.includes(val)
      ? form.property_types.filter(t => t !== val)
      : [...form.property_types, val]);
  };

  const handleSubmit = async () => {
    if (!form.full_name || !form.phone) {
      setError("Name and phone are required");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const payload = {
        ...form,
        experience: form.experience ? parseInt(form.experience) : null,
        company_name: form.company_name || null,
        entity_type: form.entity_type || null,
        ein: form.ein || null,
        company_address: form.company_address || null,
      };
      const res = await axios.post(`${API}/auth/complete-onboarding`, payload);
      onComplete(res.data.user);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Failed to save. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center" data-testid="onboarding-page">
      <div className="absolute inset-0 bg-emerald-950" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(229,201,139,0.06),transparent_60%)]" />

      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-xl mx-auto px-4 py-8 sm:px-6 sm:py-10">
        <div className="text-center mb-8">
          <Logo size="small" showTagline={false} />
          <h2 className="text-2xl font-bold text-white mt-6">Set Up Your Account</h2>
          <p className="text-sm text-cream/50 mt-2">Complete your profile to get started with deal submissions and lender matching.</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map(s => (
            <div key={s} className="flex items-center gap-2">
              <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                step >= s ? "bg-gold text-[#022c22]" : "bg-emerald-800/50 text-cream/40 border border-emerald-700/50"
              }`}>{s}</div>
              {s < 3 && <div className={`w-10 h-0.5 rounded ${step > s ? "bg-gold" : "bg-emerald-800/50"}`} />}
            </div>
          ))}
        </div>

        <div className="rounded-2xl border border-emerald-700/30 bg-emerald-900/30 backdrop-blur-xl p-6 space-y-5">
          {/* Step 1: Personal Info */}
          {step === 1 && (
            <motion.div key="s1" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <User className="h-5 w-5 text-gold" />
                <h3 className="text-lg font-bold text-white">Personal Information</h3>
              </div>
              <label className="block">
                <span className={labelClass}>Full Name *</span>
                <input required value={form.full_name} onChange={e => set("full_name", e.target.value)}
                  className={inputClass} placeholder="Your full name" data-testid="onboard-name" />
              </label>
              <label className="block">
                <span className={labelClass}>Phone Number *</span>
                <input required value={form.phone} onChange={e => set("phone", e.target.value)}
                  className={inputClass} placeholder="(555) 000-0000" data-testid="onboard-phone" />
              </label>
              <label className="block">
                <span className={labelClass}>Street Address</span>
                <input value={form.address} onChange={e => set("address", e.target.value)}
                  className={inputClass} placeholder="123 Main St" data-testid="onboard-address" />
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <label className="block">
                  <span className={labelClass}>City</span>
                  <input value={form.city} onChange={e => set("city", e.target.value)}
                    className={inputClass} placeholder="Houston" data-testid="onboard-city" />
                </label>
                <label className="block">
                  <span className={labelClass}>State</span>
                  <select value={form.state} onChange={e => set("state", e.target.value)}
                    className={inputClass} data-testid="onboard-state">
                    <option value="">Select</option>
                    {STATES.map(st => <option key={st} value={st}>{st}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className={labelClass}>Zip</span>
                  <input value={form.zip_code} onChange={e => set("zip_code", e.target.value)}
                    className={inputClass} placeholder="77001" data-testid="onboard-zip" />
                </label>
              </div>
              <GoldButton onClick={() => setStep(2)} className="w-full !rounded-xl" data-testid="onboard-next-1">
                <span className="flex items-center justify-center gap-2">Continue <ChevronRight className="h-4 w-4" /></span>
              </GoldButton>
            </motion.div>
          )}

          {/* Step 2: Investment Experience */}
          {step === 2 && (
            <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <Building2 className="h-5 w-5 text-gold" />
                <h3 className="text-lg font-bold text-white">Investment Experience</h3>
              </div>
              <label className="block">
                <span className={labelClass}>Number of Projects Completed</span>
                <input type="number" value={form.experience} onChange={e => set("experience", e.target.value)}
                  className={inputClass} placeholder="0" data-testid="onboard-experience" />
              </label>
              <div>
                <span className={labelClass}>Property Types (select all that apply)</span>
                <div className="flex gap-2 flex-wrap mt-1.5">
                  {PROPERTY_TYPES.map(pt => (
                    <button key={pt} type="button" onClick={() => togglePropType(pt)} data-testid={`onboard-pt-${pt.replace(/[\s\/]/g, "-").toLowerCase()}`}
                      className={`px-3.5 py-2 rounded-xl border text-xs font-medium transition-all ${
                        form.property_types.includes(pt) ? "border-gold bg-gold/20 text-gold" : "border-emerald-800/30 text-cream/50 hover:border-emerald-700/50"
                      }`}>{pt}</button>
                  ))}
                </div>
              </div>
              <label className="block">
                <span className={labelClass}>Investment Strategy</span>
                <select value={form.investment_strategy} onChange={e => set("investment_strategy", e.target.value)}
                  className={inputClass} data-testid="onboard-strategy">
                  <option value="">Select your primary strategy</option>
                  {STRATEGIES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <div className="flex gap-3">
                <button onClick={() => setStep(1)} className="flex-1 py-3 rounded-xl border border-emerald-700/30 text-cream/60 text-sm font-medium hover:text-cream transition-colors" data-testid="onboard-back-2">
                  Back
                </button>
                <GoldButton onClick={() => setStep(3)} className="flex-1 !rounded-xl" data-testid="onboard-next-2">
                  <span className="flex items-center justify-center gap-2">Continue <ChevronRight className="h-4 w-4" /></span>
                </GoldButton>
              </div>
            </motion.div>
          )}

          {/* Step 3: Company (Optional) */}
          {step === 3 && (
            <motion.div key="s3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-gold" />
                  <h3 className="text-lg font-bold text-white">Company Information</h3>
                </div>
                <span className="px-2.5 py-0.5 rounded-full bg-emerald-800/50 text-cream/40 text-[10px] uppercase tracking-wider border border-emerald-700/30">Optional</span>
              </div>
              <p className="text-xs text-cream/40">If you invest through a business entity, enter it here. You can skip this and add it later.</p>
              <label className="block">
                <span className={labelClass}>Company / Entity Name</span>
                <input value={form.company_name} onChange={e => set("company_name", e.target.value)}
                  className={inputClass} placeholder="Your LLC or Corp name" data-testid="onboard-company" />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className={labelClass}>Entity Type</span>
                  <select value={form.entity_type} onChange={e => set("entity_type", e.target.value)}
                    className={inputClass} data-testid="onboard-entity-type">
                    <option value="">Select</option>
                    {ENTITY_TYPES.map(et => <option key={et} value={et}>{et}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className={labelClass}>EIN</span>
                  <input value={form.ein} onChange={e => set("ein", e.target.value)}
                    className={inputClass} placeholder="XX-XXXXXXX" data-testid="onboard-ein" />
                </label>
              </div>
              <label className="block">
                <span className={labelClass}>Company Address</span>
                <input value={form.company_address} onChange={e => set("company_address", e.target.value)}
                  className={inputClass} placeholder="Company address" data-testid="onboard-company-address" />
              </label>

              {error && <div className="rounded-xl border border-red-400/30 bg-red-500/10 p-3 text-sm text-red-300">{error}</div>}

              <div className="flex gap-3">
                <button onClick={() => setStep(2)} className="flex-1 py-3 rounded-xl border border-emerald-700/30 text-cream/60 text-sm font-medium hover:text-cream transition-colors" data-testid="onboard-back-3">
                  Back
                </button>
                <GoldButton onClick={handleSubmit} disabled={loading} className="flex-1 !rounded-xl" data-testid="onboard-submit">
                  {loading ? <span className="flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Saving...</span>
                    : "Complete Setup"}
                </GoldButton>
              </div>
              <button onClick={handleSubmit} disabled={loading}
                className="w-full text-center text-xs text-cream/30 hover:text-cream/50 transition-colors" data-testid="onboard-skip">
                Skip for now
              </button>
            </motion.div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
